import {isPermissionActive} from '../../../permissions/permissionMetadata'

export type BriefingTopic = {
  id: string
  title: string
  icon: string
  route: string
  permission: string
}

export type BriefingTopicPermission = {
  name: string
  is_active?: boolean | number | string | null
}

export type BriefingTopicGroup = {
  id: string
  title: string
  description: string
  icon: string
  route: string
  topicIds: readonly string[]
}

const topicDefinitions = [
  {id: 'cancelamento-encerramento', title: 'Cancelamento / Encerramento'},
  {id: 'reativacao', title: 'Reativação'},
  {id: 'alteracao-budget', title: 'Alteração de budget'},
  {id: 'budget-atrelado', title: 'Budget atrelado'},
  {id: 'alteracao-cnpj', title: 'Alteração de CNPJ'},
  {id: 'alteracao-segmento', title: 'Alteração de Segmento na Promoção'},
  {id: 'alteracao-ean', title: 'Alteração de EAN'},
  {id: 'troca-familia', title: 'Troca de família'},
  {id: 'alteracao-seller', title: 'Alteração de Seller'},
  {id: 'alteracao-vigencia', title: 'Alteração de vigência'},
  {id: 'criacao-segmentacao-recorrente', title: 'Criação de nova segmentação recorrente'},
  // Mantém o identificador para preservar solicitações, anexos e permissões existentes.
  {id: 'criacao-segmentacao-recompra', title: 'Criação de segmentação de compra'},
  {id: 'criacao-familia', title: 'Criação de família'},
  {id: 'alteracao-desconto', title: 'Alteração de Desconto'},
  {id: 'alteracao-valor-minimo-maximo', title: 'Alteração de Valor mínimo e máximo'},
  {id: 'alteracao-quantidade-minima-maxima', title: 'Alteração de Quantidade mínima e máxima'},
  {id: 'alteracao-outros-campos', title: 'Outros campos'},
  {id: 'alteracao-icone-lista-sugerida', title: 'Ícone na lista sugerida'},
  {id: 'cupom-visivel-carrinho', title: 'Cupom visível no carrinho'},
]

const iconPath = '/media/icons/duotune/menu/briefing-topics/'

export const briefingTopics: BriefingTopic[] = topicDefinitions.map((topic) => ({
  ...topic,
  icon:
    topic.id === 'cupom-visivel-carrinho'
      ? '/media/icons/duotune/menu/briefing-promotion.svg'
      : iconPath + topic.id + '.svg',
  route: '/briefing-promocoes/topicos/' + topic.id,
  permission: 'briefing-promocoes-topicos-' + topic.id + '-view',
}))

export const briefingTopicGroups: BriefingTopicGroup[] = [
  {
    id: 'gerenciar-promocao',
    title: 'Gerenciar promoção',
    description: 'Gerencie o status, a vigência e a visibilidade das promoções.',
    icon: iconPath + 'reativacao.svg',
    route: '/briefing-promocoes/topicos/gerenciar-promocao',
    topicIds: [
      'cancelamento-encerramento',
      'reativacao',
      'alteracao-vigencia',
      'cupom-visivel-carrinho',
    ],
  },
  {
    id: 'budget-condicoes',
    title: 'Budget e condições',
    description: 'Ajuste budgets, descontos e os limites de valor e quantidade.',
    icon: iconPath + 'alteracao-budget.svg',
    route: '/briefing-promocoes/topicos/budget-condicoes',
    topicIds: [
      'alteracao-budget',
      'budget-atrelado',
      'alteracao-desconto',
      'alteracao-valor-minimo-maximo',
      'alteracao-quantidade-minima-maxima',
    ],
  },
  {
    id: 'cadastro-apresentacao',
    title: 'Cadastro e apresentação',
    description: 'Atualize os dados de cadastro e a apresentação das promoções.',
    icon: iconPath + 'alteracao-outros-campos.svg',
    route: '/briefing-promocoes/topicos/cadastro-apresentacao',
    topicIds: [
      'alteracao-cnpj',
      'alteracao-ean',
      'alteracao-seller',
      'alteracao-icone-lista-sugerida',
      'alteracao-outros-campos',
    ],
  },
  {
    id: 'segmentacao',
    title: 'Segmentação',
    description: 'Altere segmentos ou solicite novas segmentações recorrentes e de compra.',
    icon: iconPath + 'alteracao-segmento.svg',
    route: '/briefing-promocoes/topicos/segmentacao',
    topicIds: [
      'alteracao-segmento',
      'criacao-segmentacao-recorrente',
      'criacao-segmentacao-recompra',
    ],
  },
  {
    id: 'familias',
    title: 'Famílias',
    description: 'Troque famílias de uma promoção ou solicite a criação de uma família.',
    icon: iconPath + 'criacao-familia.svg',
    route: '/briefing-promocoes/topicos/familias',
    topicIds: ['troca-familia', 'criacao-familia'],
  },
]

export function getAvailableBriefingTopics(permissions: readonly BriefingTopicPermission[] = []) {
  const permissionNames = new Set(
    permissions.filter(isPermissionActive).map((permission) => permission.name)
  )
  return briefingTopics.filter((topic) => permissionNames.has(topic.permission))
}

export function getAvailableBriefingTopicGroups(
  permissions: readonly BriefingTopicPermission[] = []
) {
  const available = new Map(
    getAvailableBriefingTopics(permissions).map((topic) => [topic.id, topic])
  )
  return briefingTopicGroups
    .map((group) => ({
      ...group,
      topics: group.topicIds
        .map((id) => available.get(id))
        .filter((topic): topic is BriefingTopic => !!topic),
    }))
    .filter((group) => group.topics.length > 0)
}

export function getBriefingTopicLocation(topicId: string): string {
  const group = briefingTopicGroups.find((item) => item.topicIds.includes(topicId))
  return group ? group.route + '?topico=' + encodeURIComponent(topicId) : '/error/404'
}
